// Picking files from Google Drive to use as context. People choose files in Google's own
// picker and MakeMeA can read only those (the drive.file permission), so it needs no
// access to anything else. It switches on once the Google keys are set for the build.
const API_KEY = process.env.VUE_APP_GOOGLE_API_KEY;
const CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;
const APP_ID = process.env.VUE_APP_GOOGLE_APP_ID;

export const driveAvailable = Boolean(API_KEY && CLIENT_ID);

// Google's own formats are exported as text MakeMeA can read; other files come as they are
const EXPORTS = {
  "application/vnd.google-apps.document": ["text/plain", ".txt"],
  "application/vnd.google-apps.spreadsheet": ["text/csv", ".csv"],
  "application/vnd.google-apps.presentation": ["text/plain", ".txt"],
};
const READABLE = [
  ...Object.keys(EXPORTS),
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "text/markdown",
  "text/html",
  "image/png",
  "image/jpeg",
  "image/webp",
];

const scripts = {};
function loadScript(src) {
  scripts[src] ||= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => {
      delete scripts[src];
      reject(new Error("Couldn't reach Google"));
    };
    document.head.appendChild(script);
  });
  return scripts[src];
}

async function accessToken() {
  await loadScript("https://accounts.google.com/gsi/client");
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: (response) =>
        response.access_token
          ? resolve(response.access_token)
          : reject(new Error(response.error || "Google didn't allow it")),
      error_callback: () => reject(new Error("Google sign-in was closed")),
    });
    client.requestAccessToken({ prompt: "" });
  });
}

async function chooseFiles(token) {
  await loadScript("https://apis.google.com/js/api.js");
  await new Promise((resolve) => window.gapi.load("picker", resolve));
  const { picker } = window.google;
  return new Promise((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(false)
      .setMimeTypes(READABLE.join(","));
    const builder = new picker.PickerBuilder()
      .addView(view)
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setCallback((data) => {
        if (data.action === picker.Action.PICKED) resolve(data.docs || []);
        if (data.action === picker.Action.CANCEL) resolve([]);
      });
    if (APP_ID) builder.setAppId(APP_ID);
    builder.build().setVisible(true);
  });
}

async function download(file, token) {
  const exported = EXPORTS[file.mimeType];
  const url = exported
    ? `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(
        file.id
      )}/export?mimeType=${encodeURIComponent(exported[0])}`
    : `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(
        file.id
      )}?alt=media`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Couldn't open ${file.name}`);
  const blob = await response.blob();
  const name = exported ? `${file.name}${exported[1]}` : file.name;
  return new File([blob], name, { type: blob.type || file.mimeType });
}

// Resolves to the chosen files, ready to read like uploads (none if cancelled)
export async function pickFromDrive() {
  const token = await accessToken();
  const chosen = await chooseFiles(token);
  return Promise.all(chosen.map((file) => download(file, token)));
}
